import { Group } from '../editAssessment/Dto/EditAssessmentDTO';
import ChiefComplaintDTO from '../editAssessment/Dto/ChiefComplaintDTO';
import { makeSerializable } from '../../utils/Utils';
import CONSTANT from '../../constants/Constant';
import GLOBAL from '../../global';

export const CC_RESPONSE_TYPE = 'CC_MODAL';

/**
 * Flattens all groups/subGroups into a single ordered question list.
 * Tags each question with its groupId for later saving.
 * Inserts the CC question at the Chief Complaints group position.
 * Skips the Biometric group.
 */
export const flattenGroupsToQuestions = (groups, ccQuestion) => {
    const flatQuestions = [];

    const sortedGroups = [...groups].sort((a, b) => a.viewOrderId - b.viewOrderId);

    for (const group of sortedGroups) {
        // For Chief Complaints group, insert the CC question
        // Do this BEFORE the empty subGroups check, since CC may have empty HeaderSectionView
        if (group.groupId === CONSTANT.ASSESSMENT_GROUP.CHIEF_COMPLAINTS.key && ccQuestion?.questionId) {
            flatQuestions.push({
                ...ccQuestion,
                responseType: CC_RESPONSE_TYPE,
                isDefaultHide: false,
                groupId: group.groupId,
                groupName: group.groupName,
                header1: '',
            });
            // If CC group has subgroups, also process those questions
            if (group.subGroups && group.subGroups.length > 0) {
                for (const subGroup of group.subGroups) {
                    for (const question of subGroup.questions) {
                        flatQuestions.push({
                            ...question,
                            groupId: group.groupId,
                            groupName: group.groupName,
                            header1: subGroup.header1 || ''
                        });
                    }
                }
            }
            continue; // Skip the empty subGroups check for CC group
        }

        // Skip groups with no subgroups (empty HeaderSectionView)
        if (!group.subGroups || group.subGroups.length === 0) continue;

        for (const subGroup of group.subGroups) {
            for (const question of subGroup.questions) {
                flatQuestions.push({
                    ...question,
                    groupId: group.groupId,
                    groupName: group.groupName,
                    header1: subGroup.header1 || ''
                });
            }
        }
    }

    return flatQuestions;
};

export const isQuestionAnswered = (question) => {
    if (!question) return false;
    const { responseType, answerId, answerValue, answerList } = question;
    if (responseType === CONSTANT.ELEMENT_TYPES.CHECKBOX || responseType === CONSTANT.ELEMENT_TYPES.MULTISELECT) {
        return (answerList ?? []).length > 0;
    }
    return (answerId ?? 0) !== 0 || (answerValue ?? '') !== '';
};

/**
 * Initialises chatbotNew Redux state from the API response.
 * Called from the slice's extraReducer on thunk fulfilled.
 */
export const initChatbotNewFromApiResponse = (state, { testId, message, tenantFeatures = {} }) => {
    if (!message) {
        state.phase = 'error';
        state.error = 'No assessment data received';
        return;
    }

    const ccData = message.CheifComplaintsQuestion;
    const ccQuestion = ccData ? makeSerializable(new ChiefComplaintDTO(ccData)) : {};

    let groupInstances = (message.ExamStageQuestionView || []).map(
        group => makeSerializable(new Group(group))
    );

    // If CC question exists but no group with GroupId 4, create a synthetic group
    const hasCCGroup = groupInstances.some(
        group => group.groupId === CONSTANT.ASSESSMENT_GROUP.CHIEF_COMPLAINTS.key
    );

    if (ccQuestion?.questionId && !hasCCGroup) {
        // Create synthetic Chief Complaints group
        const syntheticCCGroup = makeSerializable(new Group({
            GroupId: CONSTANT.ASSESSMENT_GROUP.CHIEF_COMPLAINTS.key,
            ViewOrderId: CONSTANT.ASSESSMENT_GROUP.CHIEF_COMPLAINTS.viewOrder,
            GroupName: CONSTANT.ASSESSMENT_GROUP.CHIEF_COMPLAINTS.name,
            GroupHeaderTitle: '',
            CompletedGroupHeaderTitle: '',
            GroupCompleted: false,
            TotalQuestions: 1,
            QuestionsAnswered: 0,
            HeaderSectionView: [] // Empty, CC question will be inserted separately
        }));
        groupInstances.push(syntheticCCGroup);
    }

    const { isCautionQuestionEnable = true } = tenantFeatures;
    const biometricGroupData = message.BiometricQuestionView;

    if (isCautionQuestionEnable && biometricGroupData && biometricGroupData.HeaderSectionView && biometricGroupData.HeaderSectionView.length > 0) {
        const headerSectionViewMapped = biometricGroupData.HeaderSectionView.map(hsv => {
            return {
                Header1: hsv.Header1 || '',
                ExamResponse: (hsv.ExamResponse || []).map(q => ({
                    ...q,
                    QuestionTitle: q.QuestionTitle ? q.QuestionTitle.replace(/^\*\s*/, "") : q.QuestionTitle,
                    UserEditResponse: true
                }))
            };
        });

        const biometricGroup = makeSerializable(new Group({
            GroupId: CONSTANT.ASSESSMENT_GROUP.BIOMETRIC.key,
            ViewOrderId: CONSTANT.ASSESSMENT_GROUP.BIOMETRIC.viewOrder,
            GroupName: CONSTANT.ASSESSMENT_GROUP.BIOMETRIC.name,
            GroupHeaderTitle: biometricGroupData.GroupHeaderDescription || '',
            CompletedGroupHeaderTitle: biometricGroupData.GroupCompletedMessage || '',
            GroupCompleted: false,
            TotalQuestions: headerSectionViewMapped.reduce((acc, h) => acc + (h.ExamResponse?.length || 0), 0),
            QuestionsAnswered: 0,
            HeaderSectionView: headerSectionViewMapped
        }));
        groupInstances.push(biometricGroup);
    }

    const flatQuestions = flattenGroupsToQuestions(groupInstances, ccQuestion);

    const answeredQuestions = [];
    let firstUnansweredIndex = -1;

    for (let i = 0; i < flatQuestions.length; i++) {
        const q = flatQuestions[i];
        if (q.isDefaultHide) continue;
        if (isQuestionAnswered(q)) {
            answeredQuestions.push({ ...q });

            // Exit Workflow Check: if any answered question has a response that terminates the workflow
            const selectedResponse = (q.responses || []).find(r => Number(r.id) == Number(q.answerId));
            if (selectedResponse?.isExitWorkflow) {
                state.isBlocked = true;
                state.exitWorkflowMessage = selectedResponse.exitWorkflowMessage;
                state.phase = 'blocked';
                break;
            }

            // Caution Check (Biometric Group)
            if (q.groupId === CONSTANT.ASSESSMENT_GROUP.BIOMETRIC.key && selectedResponse && (selectedResponse.name || '').toLowerCase() === 'yes') {
                state.showBiometricCautionAlert = true;
                break;
            }
        } else {
            firstUnansweredIndex = i;
            break;
        }
    }

    state.testId = testId;
    state.invitationId = message.InvitationId || '';
    state.invitationTypeName = (message.InvitationTypeName || '').toLowerCase();
    state.bodyRegionList = message.BodyRegionList || [];
    state.groups = groupInstances;
    state.flatQuestions = flatQuestions;
    state.ccQuestion = ccQuestion;
    state.answeredQuestions = answeredQuestions;
    state.editingQuestionIndex = -1;
    state.biometricGroupHeaderDescription = message.BiometricQuestionView?.GroupHeaderDescription || '';
    state.tenantFeatures = tenantFeatures;

    if (state.phase === 'blocked') {
        // Stay blocked
    } else if (firstUnansweredIndex === -1) {
        state.phase = 'completed';
        state.currentQuestionIndex = flatQuestions.length - 1;

        const { isExamineticsEnabled } = tenantFeatures;
        const isSelfAssessment = state.invitationTypeName !== CONSTANT.REDIRECT_TYPE.INCLINIC && state.invitationTypeName !== CONSTANT.REDIRECT_TYPE.TELEMEDICINE;

        if (isExamineticsEnabled && isSelfAssessment) {
            state.showBiometricPrompt = true;
            state.biometricModalOpen = true;
        }
    } else {
        state.phase = 'active';
        state.currentQuestionIndex = firstUnansweredIndex;
    }
}

/**
 * Builds the answers array expected by saveSingleQuestionAnswer.
 */
export const buildAnswersPayload = (question, responseId, responseValue) => {
    const { questionId, responseType, answerList, groupId } = question;

    if (responseType === CONSTANT.ELEMENT_TYPES.CHECKBOX || responseType === CONSTANT.ELEMENT_TYPES.MULTISELECT) {
        return [{ AnswerList: answerList ?? [], QuestionId: questionId }];
    }

    return [{
        GroupId: groupId ?? CONSTANT.ASSESSMENT_GROUP.DEMOGRAPHICS.key,
        AnswerId: responseId || 0,
        AnswerTitle: responseValue || '',
        QuestionId: questionId,
    }];
};
